import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveInstallation,
  getInstallation,
  listFieldInstallers,
  rejectInstallation,
} from './api';
import type { ApproveInstallationBody, RejectInstallationValues } from './types';

export const installationKeys = {
  all: ['installations'] as const,
  metaFieldInstallers: (search?: string) =>
    [...installationKeys.all, 'meta', 'field-installers', search ?? ''] as const,
  detail: (orderId: string) => [...installationKeys.all, 'detail', orderId] as const,
};

/** Assignee filter options (the full field-installer directory, no search); cached 5 min
 *  (changes rarely). */
export function useInstallationAssignees(enabled = true) {
  return useQuery({
    queryKey: installationKeys.metaFieldInstallers(),
    queryFn: () => listFieldInstallers(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Single installation-request detail, keyed by `orderId`; disabled when `orderId` is empty. */
export function useInstallation(orderId: string) {
  return useQuery({
    queryKey: installationKeys.detail(orderId),
    queryFn: () => getInstallation(orderId),
    enabled: Boolean(orderId),
  });
}

/** Rejects the installation request with an admin-entered reason. The real backend's reject
 *  response is slim (`{orderId, status}`), so this invalidates rather than seeds the detail
 *  cache — the drawer refetches the full detail on the next read. */
export function useRejectInstallation(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: RejectInstallationValues) => rejectInstallation(orderId, values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: installationKeys.detail(orderId) });
      void qc.invalidateQueries({ queryKey: installationKeys.all });
    },
  });
}

/** Approves the installation request (internal or external assignee). The real backend's
 *  approve response is slim and comes in two shapes (success vs. capture-failure) — the caller
 *  reads the raw result directly; this hook only invalidates so the drawer refetches. */
export function useApproveInstallation(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApproveInstallationBody) => approveInstallation(orderId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: installationKeys.detail(orderId) });
      void qc.invalidateQueries({ queryKey: installationKeys.all });
    },
  });
}
